<script setup lang="ts">
/**
 * Panneau qui monte du bas de l'écran (glisser vers le bas pour fermer). Séparé du contenu par
 * un voile et un filet, sans ombre. Titre obligatoire (lecteurs d'écran). Contenu défilable,
 * pied de page fixe pour l'action principale, au pouce.
 */
import {
  DrawerContent,
  DrawerDescription,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
} from 'vaul-vue'

import { fr } from '@/copy/fr'

import IconButton from './IconButton.vue'

withDefaults(
  defineProps<{
    title: string
    description?: string
    hideTitle?: boolean
    dismissible?: boolean
  }>(),
  { description: undefined, hideTitle: false, dismissible: true },
)

const open = defineModel<boolean>('open', { default: false })
</script>

<template>
  <DrawerRoot v-model:open="open" :dismissible="dismissible" :should-scale-background="false">
    <slot name="trigger" />
    <DrawerPortal>
      <DrawerOverlay class="fixed inset-0 z-40 bg-scrim" />
      <DrawerContent
        class="fixed inset-x-0 bottom-0 z-50 flex max-h-sheet flex-col rounded-t-sheet border-t border-line bg-bg outline-none"
      >
        <div
          class="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-surface-strong"
          aria-hidden="true"
        />
        <header :class="['flex items-start gap-2 px-gutter pt-3', hideTitle ? 'sr-only' : 'pb-2']">
          <div class="flex flex-1 flex-col gap-1 pt-2.5">
            <DrawerTitle class="text-section text-ink">{{ title }}</DrawerTitle>
            <DrawerDescription v-if="description" class="text-label font-normal text-ink-muted">
              {{ description }}
            </DrawerDescription>
          </div>
          <IconButton v-if="dismissible" icon="close" :label="fr.ui.close" @click="open = false" />
        </header>
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-gutter pb-4">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="border-t border-line px-gutter pt-3 pb-safe">
          <div class="pb-3"><slot name="footer" /></div>
        </footer>
        <div v-else class="pb-safe" />
      </DrawerContent>
    </DrawerPortal>
  </DrawerRoot>
</template>
